// Desc: This file contains the filter component for the Inventory Map page. It contains the filter fields for the Inventory Map page.
import { Box, Card, CardContent } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { isEqual } from "lodash";
import { isValidDateObject } from "../../../globalStore/utils/helpers";
import { searchAssets } from "../../../utils/API/Assets/Assets";
import { useSelector } from "react-redux";
import AutocompleteSelect from "../../../components/Forms/FieldTypes/AutocompleteSelect";
import ClassificationFilters from "../../../components/Selects/ClassificationsSelector/ClassificationFilters";
import CustomFilters from "../../../components/Selects/CustomsSelector/CustomFilters";
import Loading from "../../../components/Loading/Loading";
import LXDateRangePicker from "../../../components/Forms/FieldTypes/DateRangePicker";
import MaterialUiButton from "../../../components/Buttons/MaterialUiButton/MaterialUiButton";
import { useState } from "react";
import SimpleTextField from "../../../components/Forms/FieldTypes/TextField";
import RadioList from "../../../components/Forms/FieldTypes/RadioList";

export default function InventoryMapFilter(props) {
  const {
    apiUrl,
    assetMode = "Inventory",
    // The exportWithCard prop is used to determine if the filters get exported with the MUI Card component
    exportWithCard = true,
    isInventory = true,

    // The go back prop adds another button to the filter component. This should be a { label, onClick } object.
    secondButton = null,
    organizationId,
    onSubmitEnd = () => {},
    setState,
    showAssetTypeRadioGroup = false,
    state,
    token,
    viewAssetHistory,
    viewInventory,
  } = props;
  const { filters = {} } = state;
  const {
    assetType,
    endDate,
    startDate,
    tag,
    facility = { name: "" },
  } = filters;

  const { classifications, customs, facilities } = useSelector(
    (state) => state.organization,
    isEqual
  );

  const facilityArray = facilities
    ? Object.values(facilities).sort((a, b) => {
        return (a.name || "").localeCompare(b.name || "");
      })
    : [];

  const classificationsList = Object.values(classifications || {}).filter(
    //if assetType is Inventory, only return classifications with assetMode of Inventory. If assetType is Asset , return all classifications that are not Inventory
    (classification) =>
      classification.assetMode === assetType || !classification.assetMode
  );

  const customsList = customs.filter(
    //if assetType is Inventory, only return customs with assetMode of Inventory. If assetType is Asset , return all customs that are not Inventory
    (custom) => custom.assetMode === assetType || !custom.assetMode
  );

  const [canSubmit, setCanSubmit] = useState(true);
  //filterLoading variable to show loading spinner when filters are being applied
  const [filterLoading, setFilterLoading] = useState(false);

  function handleFilterApply() {
    setFilterLoading(true);
    //use searchAssets to filter the data in the map
    searchAssets(
      {
        apiUrl,
        token,
        organizationId,
        assetMode,
        facilityId: state.filters.facility?.facilityId,
        isInventory,
      },
      { filters: state.filters }
    ).then((results) => {
      setFilterLoading(false);
      setState((prevState) => {
        return {
          ...prevState,
          assets: results.assets,
          count: results.count,
          page: Math.floor(state.filters.start / state.filters.limit),
          mapTableData: [],
        };
      });
      onSubmitEnd();
    });
  }

  const fieldStyle = { margin: "1rem 0" };

  const filterContent = (
    <Grid container>
      {/* //spinner to show loading when filters are being applied */}
      {filterLoading && (
        <Loading
          styles={{
            position: "absolute",
            top: "40%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        />
      )}

      {/* Date range (From – To) / Display Button */}
      <Grid sx={{ padding: "0 1rem" }} size={3}>
        <Box sx={fieldStyle}>
          <LXDateRangePicker
            format="MM/dd/yyyy"
            label="From – To Date (required)"
            onChange={(start, end) => {
              const startValid = isValidDateObject(start);
              const endValid = isValidDateObject(end);
              const rangeValid =
                !start ||
                !end ||
                start <= end ||
                start?.toDateString?.() === end?.toDateString?.();
              setCanSubmit(startValid && endValid && rangeValid);
              setState((prevState) => ({
                ...prevState,
                filters: {
                  ...prevState.filters,
                  startDate: start ?? prevState.filters.startDate,
                  endDate: end ?? prevState.filters.endDate,
                },
              }));
            }}
            slotProps={{
              textField: {
                helperText: !canSubmit && "Invalid date range",
                size: "large",
              },
            }}
            value={[startDate, endDate]}
          />
        </Box>

        {/* Display Button */}
        <Box sx={{ marginTop: "1rem" }}>
          <MaterialUiButton
            data-testid="Display Button"
            fullWidth
            label="Apply Filters"
            onClick={() => handleFilterApply()}
          />
          {secondButton ? (
            <MaterialUiButton
              data-testid="Display Button"
              fullWidth
              label={secondButton?.label}
              onClick={() => secondButton?.onClick()}
              sx={{ marginTop: ".5rem" }}
            />
          ) : null}
        </Box>
      </Grid>

      {/* Row/Bin / Event */}
      <Grid sx={{ padding: "0 1rem" }} size={3}>
        {/* Tag ID */}
        <Box sx={fieldStyle}>
          <SimpleTextField
            onChange={(event) =>
              setState((prevState) => ({
                ...prevState,
                filters: {
                  ...prevState.filters,
                  tag: event.target.value,
                },
              }))
            }
            fullWidth
            size="large"
            value={tag}
            label="Tag Id"
          />
        </Box>

        {/* Facility */}
        <Box sx={fieldStyle}>
          <AutocompleteSelect
            id="facilityName"
            getOptionLabel={(option) => option.name}
            label="Facility Name"
            onChange={(event, newValue) => {
              setState((prevState) => ({
                ...prevState,
                filters: {
                  ...prevState.filters,
                  facility: newValue,
                },
              }));
            }}
            options={facilityArray}
            size="large"
            value={facility}
            variant="outlined"
          />
        </Box>

        {/* Asset Type Radio Group */}
        {showAssetTypeRadioGroup ? (
          <Box sx={fieldStyle}>
            <RadioList
              label="Asset Type"
              name="assetType"
              onChange={(event) => {
                setState((prevState) => ({
                  ...prevState,
                  filters: {
                    ...prevState.filters,
                    assetType: event.target.value,
                  },
                }));
              }}
              options={[
                {
                  label: "Inventory",
                  value: "Inventory",
                  disabled: !viewInventory,
                },
                { label: "Asset", value: "Asset", disabled: !viewAssetHistory },
              ]}
              row
              value={state?.filters?.assetType}
            />
          </Box>
        ) : null}
      </Grid>

      {/* Classification Filters */}
      <ClassificationFilters
        classifications={classificationsList}
        state={state}
        setState={setState}
      />

      {/* Custom Filters */}
      <CustomFilters customs={customsList} state={state} setState={setState} />
    </Grid>
  );

  return exportWithCard ? (
    <Card>
      <CardContent>{filterContent}</CardContent>
    </Card>
  ) : (
    filterContent
  );
}
